import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

const BulkRoomModal = ({ open, onClose, propertyId, unitTypes, existingUnits, onSuccess }) => {
  const [unitTypeId, setUnitTypeId] = useState('');
  const [quantity, setQuantity] = useState(10);
  const [prefix, setPrefix] = useState('');
  const [startNumber, setStartNumber] = useState(101);
  const [suffix, setSuffix] = useState('');
  const [floor, setFloor] = useState('');
  const [smokingPolicy, setSmokingPolicy] = useState('NON_SMOKING');
  const [copyFromUnitId, setCopyFromUnitId] = useState('');
  const [previewNames, setPreviewNames] = useState([]);

  // Debug logging and initialize unitTypeId when modal opens
  useEffect(() => {
    if (open) {
      console.log('[BulkRoomModal] Modal opened');
      console.log('[BulkRoomModal] unitTypes:', unitTypes);
      console.log('[BulkRoomModal] unitTypes length:', unitTypes?.length);
      console.log('[BulkRoomModal] unitTypes sample:', unitTypes?.[0]);
      
      // Auto-select first unit type if available
      if (unitTypes && unitTypes.length > 0 && unitTypes[0].id) {
        setUnitTypeId(unitTypes[0].id);
      }
    }
  }, [open, unitTypes]);

  useEffect(() => {
    generatePreview();
  }, [quantity, prefix, startNumber, suffix]);

  const generatePreview = () => {
    const names = [];
    for (let i = 0; i < Math.min(quantity, 20); i++) {
      const number = startNumber + i;
      const name = `${prefix}${number}${suffix}`;
      names.push(name);
    }
    setPreviewNames(names);
  };

  const handleSubmit = async () => {
    if (!unitTypeId) {
      toast.error('Please select a room type');
      return;
    }

    if (quantity < 1 || quantity > 500) {
      toast.error('Quantity must be between 1 and 500');
      return;
    }

    // Generate all room names
    const rooms = [];
    for (let i = 0; i < quantity; i++) {
      const number = startNumber + i;
      const name = `${prefix}${number}${suffix}`;
      rooms.push({
        name,
        floor: floor ? parseInt(floor) : null,
        smoking_policy: smokingPolicy,
        tags: []
      });
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/properties/${propertyId}/units/bulk`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            unit_type_id: unitTypeId,
            rooms,
            copy_from_unit_id: copyFromUnitId || null
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create rooms');
      }

      const result = await response.json();
      
      let message = `Created ${result.created.length} room(s).`;
      if (result.skipped.length > 0) {
        message += ` Skipped ${result.skipped.length} existing room(s): ${result.skipped.join(', ')}.`;
      }
      
      toast.success(message);
      onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Error creating bulk rooms:', error);
      toast.error(error.message || 'Failed to create rooms');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Multiple Rooms</DialogTitle>
        </DialogHeader>
        
        {!unitTypes || unitTypes.length === 0 ? (
          <div className="p-6 text-center space-y-4">
            <p className="text-slate-600">
              No room types available. Please create a room type in the "Room Types" tab first.
            </p>
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="unit-type">Room Type *</Label>
              <select
                id="unit-type"
                value={unitTypeId}
                onChange={(e) => setUnitTypeId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                required
              >
                <option value="">Select room type</option>
                {unitTypes.filter(type => type.id).map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

          <div>
            <Label htmlFor="quantity">How many rooms? *</Label>
            <Input
              id="quantity"
              type="number"
              min="1"
              max="500"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
            />
            <p className="text-xs text-slate-500 mt-1">Maximum 500 rooms per operation</p>
          </div>

          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-3">Naming Pattern</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="prefix">Prefix</Label>
                <Input
                  id="prefix"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  placeholder="R"
                />
              </div>
              <div>
                <Label htmlFor="start-number">Starting Number *</Label>
                <Input
                  id="start-number"
                  type="number"
                  value={startNumber}
                  onChange={(e) => setStartNumber(e.target.value === '' ? '' : parseInt(e.target.value) || 1)}
                />
              </div>
              <div>
                <Label htmlFor="suffix">Suffix</Label>
                <Input
                  id="suffix"
                  value={suffix}
                  onChange={(e) => setSuffix(e.target.value)}
                  placeholder="A"
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="floor">Floor (optional)</Label>
            <Input
              id="floor"
              type="number"
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              placeholder="1"
            />
          </div>

          <div>
            <Label htmlFor="smoking-policy">Smoking Policy</Label>
            <select
              id="smoking-policy"
              value={smokingPolicy}
              onChange={(e) => setSmokingPolicy(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2"
            >
              <option value="NON_SMOKING">Non-Smoking</option>
              <option value="SMOKING">Smoking Allowed</option>
            </select>
          </div>

          <div>
            <Label htmlFor="copy-from">Copy settings from room (optional)</Label>
            <select
              id="copy-from"
              value={copyFromUnitId}
              onChange={(e) => setCopyFromUnitId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">None</option>
              {existingUnits && existingUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Copies amenities and settings (not name or number)
            </p>
          </div>

          {/* Preview */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-2">Preview of room names:</p>
            <div className="p-3 bg-slate-50 rounded-lg max-h-32 overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {previewNames.map((name, idx) => (
                  <Badge key={idx} variant="outline">{name}</Badge>
                ))}
                {quantity > 20 && (
                  <Badge variant="outline">... and {quantity - 20} more</Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSubmit} className="flex-1">
              <Plus className="mr-2" size={16} />
              Create {quantity} Room{quantity !== 1 ? 's' : ''}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkRoomModal;